import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SharePartnerDialog } from "@/components/channel-partners/SharePartnerDialog";
import { AssociatedTasks } from "@/components/dashboard/AssociatedTasks";
import { AssociatedLeads } from "@/components/dashboard/AssociatedLeads";
import { useLeads } from "@/hooks/useLeads";
import { useTasks } from "@/hooks/useTasks";
import { useDsaPartners } from "@/hooks/useDsaPartners";
import { useAuth } from "@/contexts/AuthContext";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useFileUpload } from "@/hooks/useFileUpload";
import { getAllActiveUsers } from "@/services/userService";
import { addDsaChangeHistoryEntry } from "@/services/dsaPartnerService";
import {
  getCurrentLocation,
  calculateDistance,
} from "@/services/geolocationService";
import {
  INDIAN_STATES,
  INDIAN_CITIES,
  TEST_ADMIN_USER_ID,
} from "@/lib/constants";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { FileUploadSection } from "@/components/common/FileUploadSection";
import { ImageViewer } from "@/components/common/ImageViewer";
import {
  DsaPartner,
  DsaPartnerType,
  Lead,
  Task,
  LOAN_STATUS_CONFIG,
  DSA_PARTNER_TYPE_LABELS,
  getLegacyCustomerName,
  PartnerDocument,
} from "@/types";
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Star,
  StarOff,
  Edit,
  CheckCircle,
  Share2,
  Eye,
  Calendar,
  Building2,
  FileText,
  Image,
  X,
  Navigation,
  AlertCircle,
  User,
  Building,
  Map,
  Download,
  Maximize2,
  Minimize2,
  Loader2 as Loader2Icon,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { format, differenceInDays, differenceInMonths } from "date-fns";
import { motion } from "framer-motion";
import { MapLocationPicker } from "@/components/common/MapLocationPicker";
import { usePinchZoom } from "@/hooks/usePinchZoom";

// Sub-component for Visit & Follow-up section
interface VisitFollowUpCardProps {
  connector: DsaPartner;
  setConnector: React.Dispatch<React.SetStateAction<DsaPartner | null>>;
  updatePartner: (id: string, data: Partial<DsaPartner>) => Promise<void>;
}

function VisitFollowUpCard({
  connector,
  setConnector,
  updatePartner,
}: VisitFollowUpCardProps) {
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: "visit" | "followup";
  }>({ open: false, type: "visit" });
  const [isCheckingLocation, setIsCheckingLocation] = useState(false);

  // Check if visit is pending (more than 3 months since last visit)
  const isVisitPending =
    !connector.lastVisitDate ||
    differenceInMonths(new Date(), connector.lastVisitDate) >= 3;

  // Check if follow-up is pending (more than 7 days since last follow-up)
  const isFollowUpPending =
    !connector.lastFollowUpDate ||
    differenceInDays(new Date(), connector.lastFollowUpDate) > 7;

  const handleVisitClick = async () => {
    // Check if partner has saved location
    if (!connector.location) {
      toast.error(
        "Partner location not set. Please update partner's geo-location first.",
      );
      return;
    }

    setIsCheckingLocation(true);

    try {
      // Get current location using Capacitor Geolocation
      const position = await getCurrentLocation(true, 10000);

      const currentLat = position.lat;
      const currentLng = position.lng;
      const partnerLat = connector.location.lat;
      const partnerLng = connector.location.lng;

      // Calculate distance
      const distance = calculateDistance(
        currentLat,
        currentLng,
        partnerLat,
        partnerLng,
      );

      // Check if within 200 meters
      if (distance > 200) {
        toast.error(
          `You are ${Math.round(distance)} meters away from partner location. Please be within 200 meters to mark visit.`,
        );
        setIsCheckingLocation(false);
        return;
      }

      // Location verified, proceed with confirmation
      setIsCheckingLocation(false);
      setConfirmDialog({ open: true, type: "visit" });
    } catch (error) {
      setIsCheckingLocation(false);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Could not verify location. Please enable location services and try again.";
      toast.error(errorMessage);
    }
  };

  const handleFollowUpClick = () => {
    setConfirmDialog({ open: true, type: "followup" });
  };

  const confirmAction = async () => {
    if (confirmDialog.type === "visit") {
      // Marking visit also marks follow-up
      const visitUpdates = {
        totalVisits: connector.totalVisits + 1,
        lastVisitDate: new Date(),
        lastFollowUpDate: new Date(),
      };
      setConnector((prev) => (prev ? { ...prev, ...visitUpdates } : null));
      try {
        await updatePartner(connector.id, visitUpdates);
        toast.success("Visit and follow-up marked successfully");
      } catch {
        toast.error("Failed to save visit. Please try again.");
      }
    } else {
      const followUpUpdates = { lastFollowUpDate: new Date() };
      setConnector((prev) => (prev ? { ...prev, ...followUpUpdates } : null));
      try {
        await updatePartner(connector.id, followUpUpdates);
        toast.success("Follow-up marked successfully");
      } catch {
        toast.error("Failed to save follow-up. Please try again.");
      }
    }
    setConfirmDialog({ open: false, type: "visit" });
  };

  return (
    <>
      <Card>
        <CardContent className="p-4 space-y-3">
          {/* Visit Row */}
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Last Visit</p>
              <p className="text-sm font-medium truncate">
                {connector.lastVisitDate
                  ? format(connector.lastVisitDate, "MMM d, yyyy")
                  : "Never"}
              </p>
              <p className="text-xs text-muted-foreground">
                Total: {connector.totalVisits}
              </p>
            </div>
            <Button
              onClick={handleVisitClick}
              variant={isVisitPending ? "destructive" : "default"}
              size="sm"
              className="text-xs shrink-0"
              disabled={isCheckingLocation}
            >
              {isCheckingLocation ? (
                <>Checking...</>
              ) : (
                <>
                  <Eye className="h-3.5 w-3.5 mr-1" />
                  {isVisitPending ? "Visit Pending" : "Visit Done"}
                </>
              )}
            </Button>
          </div>

          {/* Follow-up Row */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Last Follow-up</p>
              <p className="text-sm font-medium truncate">
                {connector.lastFollowUpDate
                  ? format(connector.lastFollowUpDate, "MMM d, yyyy")
                  : "Never"}
              </p>
            </div>
            <Button
              variant={isFollowUpPending ? "destructive" : "accent"}
              onClick={handleFollowUpClick}
              size="sm"
              className="text-xs shrink-0"
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1" />
              {isFollowUpPending ? "Follow Up Pending" : "Follow-up Done"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog((prev) => ({ ...prev, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.type === "visit"
                ? "Confirm Visit"
                : "Confirm Follow-up"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.type === "visit"
                ? "Mark this visit as done? This will also update the follow-up date."
                : "Mark this follow-up as done?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAction}>
              Yes, Mark Done
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function DsaPartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const backRoute = location.pathname.startsWith("/tools/dsa-partners")
    ? "/tools/dsa-partners"
    : "/tools/channel-partners";
  const { leads } = useLeads();
  const { tasks } = useTasks();
  const {
    partners: connectors,
    getPartner,
    updatePartner,
    uploadDocument,
    deleteDocument,
  } = useDsaPartners();
  const { config } = useAppConfig();
  const [connector, setConnector] = useState<DsaPartner | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<DsaPartner>>({});
  const [documentsToDelete, setDocumentsToDelete] = useState<string[]>([]); // Track document IDs to delete
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [kycConfirmDialogOpen, setKycConfirmDialogOpen] = useState(false);
  const [viewingExistingImageIndex, setViewingExistingImageIndex] = useState<
    number | null
  >(null);
  const [viewingDocument, setViewingDocument] =
    useState<PartnerDocument | null>(null);
  const [docIframeLoading, setDocIframeLoading] = useState(true);
  const [docIframeError, setDocIframeError] = useState(false);
  const [docImageError, setDocImageError] = useState(false);
  const {
    zoom: docZoomLevel,
    setZoom: setDocZoomLevel,
    resetZoom: docResetZoom,
    touchHandlers: docTouchHandlers,
  } = usePinchZoom({ minZoom: 0.25, maxZoom: 5 });
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState("");
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [editMapPickerOpen, setEditMapPickerOpen] = useState(false);

  const cityOptions = INDIAN_CITIES.map((city) => ({
    value: city,
    label: city,
  }));

  const fileUploadManager = useFileUpload({
    maxSizeMB: config.maxFileUploadSizeMB,
    maxFiles: config.maxFileUploads,
    allowedFileTypes: config.allowedFileTypes,
    uploadMode: "deferred",
  });

  const fileManagerFiles = fileUploadManager.pendingFiles;
  const fileManagerReset = fileUploadManager.resetPending;

  // Load users for displaying shared user names
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await getAllActiveUsers();
        setAvailableUsers(
          usersData.filter((u) => (u.userId || u.id) !== TEST_ADMIN_USER_ID),
        );
      } catch (error) {
        console.error("Error loading users:", error);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    if (id) {
      const found = getPartner(id);
      if (found) {
        setConnector(found);
        setEditForm(found);
        fileManagerReset();
        setDocumentsToDelete([]);
      }
    }
  }, [id, connectors, getPartner]);

  if (!connector) {
    return (
      <AppLayout>
        <div className="p-4 text-center">
          <p className="text-muted-foreground">Partner not found</p>
          <Button variant="link" onClick={() => navigate(backRoute)}>
            Go back
          </Button>
        </div>
      </AppLayout>
    );
  }

  const isKYCVerified = connector.kycVerified === true;

  // Only admin and operations_user can edit location if already set;
  // any user can set it if not yet configured
  const canEditLocation =
    user?.primaryRole === "admin" ||
    user?.primaryRole === "operations_user" ||
    !connector?.location;

  // KYC Onboarding link for pending KYC
  const kycOnboardingLink = "https://ruloansreferral.page.link/NWVV";

  const handleToggleFavorite = async () => {
    if (!connector) return;
    try {
      await updatePartner(connector.id, { isFavorite: !connector.isFavorite });
      setConnector((prev) =>
        prev ? { ...prev, isFavorite: !prev.isFavorite } : null,
      );
      toast.success(
        connector.isFavorite ? "Removed from favorites" : "Added to favorites",
      );
    } catch (error) {
      toast.error("Failed to update favorite status");
    }
  };

  const validateEditForm = () => {
    if (!editForm.name?.trim()) {
      toast.error("Partner name is required");
      return false;
    }

    if (!editForm.whatsappNumber?.trim()) {
      toast.error("WhatsApp number is required");
      return false;
    }

    const normalizedPhone = (editForm.whatsappNumber || "")
      .replace(/^\+91/, "")
      .replace(/\D/g, "");
    if (!/^[6-9]\d{9}$/.test(normalizedPhone)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return false;
    }

    if (editForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    if (editForm.pincode && !/^\d{6}$/.test(editForm.pincode)) {
      toast.error("Please enter a valid 6-digit pincode");
      return false;
    }

    return true;
  };

  const handleRemoveSharedUser = async (userIdToRemove: string) => {
    if (!connector) return;

    try {
      const updatedSharedWith = (connector.sharedWith || []).filter(
        (id) => id !== userIdToRemove,
      );

      await updatePartner(connector.id, {
        sharedWith:
          updatedSharedWith.length > 0 ? updatedSharedWith : undefined,
      });

      setConnector((prev) =>
        prev
          ? {
              ...prev,
              sharedWith:
                updatedSharedWith.length > 0 ? updatedSharedWith : undefined,
            }
          : null,
      );

      const removedUser = availableUsers.find(
        (u) => (u.userId || u.id) === userIdToRemove,
      );
      toast.success(
        `Removed ${removedUser?.name || "user"} from shared access`,
      );
    } catch (error) {
      console.error("Error removing shared user:", error);
      toast.error("Failed to remove user");
    }
  };

  const handleSaveEdit = async () => {
    if (!connector) return;

    if (!validateEditForm()) {
      return;
    }

    setIsEditing(false);
    const uploadCount = fileManagerFiles.length;
    const deleteCount = documentsToDelete.length;

    try {
      // Calculate the final documents array
      let finalDocuments = connector.documents || [];

      // Delete marked documents from Firebase Storage
      if (deleteCount > 0) {
        toast.info(`Deleting ${deleteCount} document(s)...`);

        for (const docId of documentsToDelete) {
          const doc = connector.documents?.find((d) => d.id === docId);
          if (doc?.url) {
            try {
              await deleteDocument(doc.url);
            } catch (error) {
              console.error("Error deleting document:", error);
              toast.error(`Failed to delete ${doc.name}`);
            }
          }
        }

        // Remove deleted documents from the array
        finalDocuments = finalDocuments.filter(
          (d) => !documentsToDelete.includes(d.id),
        );

        toast.success(`Deleted ${deleteCount} document(s)`);
      }

      // Upload any new documents to Firebase Storage
      if (uploadCount > 0) {
        toast.info(`Uploading ${uploadCount} file(s)...`);

        const uploadedDocuments = [];
        for (const file of fileManagerFiles) {
          try {
            const uploadedDoc = await uploadDocument(file, connector.id);
            uploadedDocuments.push(uploadedDoc);
          } catch (error) {
            console.error("Error uploading document:", error);
            toast.error(`Failed to upload ${file.name}`);
          }
        }

        // Add new documents to the array
        if (uploadedDocuments.length > 0) {
          finalDocuments = [...finalDocuments, ...uploadedDocuments];
          toast.success(
            `Uploaded ${uploadedDocuments.length} file(s) successfully`,
          );
        }
      }

      // Update partner details and documents in a single call
      const { documents, secondaryNumber, ...otherEditFormFields } = editForm;
      const saveData = {
        ...otherEditFormFields,
        whatsappNumber: otherEditFormFields.whatsappNumber?.replace(/^\+91/, "")
          ? `+91${otherEditFormFields.whatsappNumber.replace(/^\+91/, "")}`
          : otherEditFormFields.whatsappNumber,
        documents: finalDocuments,
      };
      await updatePartner(connector.id, saveData);

      // Track change history — compare fields that changed
      const trackFields = [
        "name",
        "type",
        "whatsappNumber",
        "address",
        "city",
        "pincode",
        "state",
        "email",
        "remark",
      ] as const;
      const changes: { field: string; oldValue: unknown; newValue: unknown }[] =
        [];
      for (const field of trackFields) {
        const oldVal =
          (connector as unknown as Record<string, unknown>)[field] ?? "";
        const newVal = (saveData as Record<string, unknown>)[field] ?? "";
        if (String(oldVal) !== String(newVal)) {
          changes.push({ field, oldValue: oldVal, newValue: newVal });
        }
      }
      // Track location change
      const oldLoc = connector.location;
      const newLoc = saveData.location as
        | { lat: number; lng: number }
        | undefined;
      if (JSON.stringify(oldLoc) !== JSON.stringify(newLoc)) {
        changes.push({
          field: "location",
          oldValue: oldLoc ?? null,
          newValue: newLoc ?? null,
        });
      }
      if (changes.length > 0 && user) {
        await addDsaChangeHistoryEntry(connector.id, user.id, changes);
      }

      setConnector((prev) =>
        prev ? { ...prev, ...editForm, documents: finalDocuments } : null,
      );
      fileManagerReset();
      setDocumentsToDelete([]);
      toast.success("Partner details updated");
    } catch (error) {
      console.error("Error updating partner:", error);
      toast.error("Failed to update partner details");
    }
  };

  const handleMarkDocumentForDeletion = (docId: string) => {
    setDocumentsToDelete((prev) => {
      if (prev.includes(docId)) {
        return prev.filter((id) => id !== docId);
      }
      return [...prev, docId];
    });
  };

  const handleShareOnboarding = () => {
    const message = `Hey, join me on RuConnect!
India's trusted platform for Direct Selling Agents to manage loans, leads, bills, payments and payouts — all in one place.

Getting started is easy:

Step 1: Register with your mobile number and complete your onboarding:

As an individual: https://docs.ruloans.com/b2b/webapp/onboarding/individual

As a company: https://docs.ruloans.com/b2b/webapp/onboarding/company

Step 2: Access your dashboard to:
✅ Receive & manage loan leads
✅ Track disbursements and commissions
✅ Work with top banks & NBFCs
✅ Grow your business with full transparency and support

👉 Ready to join?
Join RuConnect using my referral link:

https://ms.ruloans.com/api/v3/dsa/scan-qr-code/62080491

Let's grow together with RuConnect 🚀`;
    const rawNum = connector.whatsappNumber.replace(/[^\d]/g, "");
    const whatsappNumber =
      rawNum.startsWith("91") && rawNum.length > 10 ? rawNum : `91${rawNum}`;
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleCopyOnboardingLink = () => {
    navigator.clipboard.writeText(kycOnboardingLink);
    toast.success("Onboarding link copied");
  };

  const handleMarkKYCDone = () => {
    setKycConfirmDialogOpen(true);
  };

  const confirmKYCDone = async () => {
    try {
      if (connector?.id) {
        await updatePartner(connector.id, { kycVerified: true });
      }
      setConnector((prev) => (prev ? { ...prev, kycVerified: true } : null));
      setKycConfirmDialogOpen(false);
      toast.success("KYC marked as verified");
    } catch (error) {
      console.error("Error updating KYC status:", error);
      toast.error("Failed to update KYC status");
    }
  };

  const handleDeactivateToggle = async () => {
    if (!connector) return;

    const isCurrentlyActive = connector.isActive !== false;

    if (isCurrentlyActive) {
      // Deactivating — need reason
      if (!deactivationReason.trim()) {
        toast.error("Please provide a reason for deactivation");
        return;
      }

      setIsDeactivating(true);
      try {
        await updatePartner(connector.id, {
          isActive: false,
          deactivatedAt: new Date(),
          deactivationReason: deactivationReason.trim(),
        });
        setConnector((prev) =>
          prev
            ? {
                ...prev,
                isActive: false,
                deactivatedAt: new Date(),
                deactivationReason: deactivationReason.trim(),
              }
            : null,
        );
        setDeactivateDialogOpen(false);
        setDeactivationReason("");
        toast.success(`${connector.name} has been deactivated`);
      } catch (error) {
        toast.error("Failed to deactivate partner");
      } finally {
        setIsDeactivating(false);
      }
    } else {
      // Reactivating
      setIsDeactivating(true);
      try {
        await updatePartner(connector.id, {
          isActive: true,
          deactivatedAt: undefined,
          deactivationReason: undefined,
        });
        setConnector((prev) =>
          prev
            ? {
                ...prev,
                isActive: true,
                deactivatedAt: undefined,
                deactivationReason: undefined,
              }
            : null,
        );
        toast.success(`${connector.name} has been reactivated`);
      } catch (error) {
        toast.error("Failed to reactivate partner");
      } finally {
        setIsDeactivating(false);
      }
    }
  };

  return (
    <AppLayout>
      <div
        className="flex flex-col md:h-screen md:h-dvh"
        style={{ height: "calc(var(--viewport-height) - 80px)" }}
      >
        {/* Header - Sticky */}
        <div className="sticky top-0 z-10 bg-background border-b">
          <div className="max-w-4xl mx-auto p-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(backRoute)}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg md:text-xl font-bold truncate">
                  {connector.name}
                </h1>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" className="text-xs">
                    {DSA_PARTNER_TYPE_LABELS[connector.type]}
                  </Badge>
                  {connector.createdBy &&
                    (() => {
                      const creator = availableUsers.find(
                        (u) => (u.userId || u.id) === connector.createdBy,
                      );
                      return creator ? (
                        <span className="text-xs text-muted-foreground">
                          by {creator.name}
                        </span>
                      ) : null;
                    })()}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShareDialogOpen(true)}
                  className="flex items-center justify-center h-9 w-9 rounded-lg bg-green-100 hover:bg-green-200 transition-colors"
                >
                  <Share2 className="h-5 w-5 text-green-600" />
                </button>
                <button onClick={handleToggleFavorite}>
                  {connector.isFavorite ? (
                    <Star className="h-6 w-6 text-gold fill-gold" />
                  ) : (
                    <StarOff className="h-6 w-6 text-muted-foreground" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 md:px-6 lg:px-8 pt-4 pb-24 md:pb-8 max-w-4xl mx-auto">
            <div className="space-y-4">
              {/* KYC Status & Onboarding */}
              <Card
                className={
                  isKYCVerified
                    ? "bg-success/10 border-success/20"
                    : "bg-warning/10 border-warning/20"
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isKYCVerified ? (
                        <>
                          <CheckCircle className="h-5 w-5 text-success" />
                          <span className="font-medium text-success">
                            KYC Verified
                          </span>
                        </>
                      ) : (
                        <>
                          <Building2 className="h-5 w-5 text-warning" />
                          <span className="font-medium text-warning">
                            KYC Pending
                          </span>
                        </>
                      )}
                    </div>
                    {/* Only show buttons for pending KYC */}
                    {!isKYCVerified && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={handleMarkKYCDone}
                        >
                          Mark Done
                        </Button>
                        <Button
                          size="sm"
                          variant="success"
                          onClick={handleShareOnboarding}
                        >
                          <Share2 className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">WhatsApp</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Contact Details */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Contact Details</CardTitle>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setIsEditing(true)}
                      className="h-8 w-8 p-0 rounded-full"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{connector.whatsappNumber}</span>
                    {connector.secondaryNumber && (
                      <span className="text-muted-foreground truncate">
                        / {connector.secondaryNumber}
                      </span>
                    )}
                  </div>
                  {connector.email && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{connector.email}</span>
                    </div>
                  )}
                  <div className="flex items-start gap-3 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="truncate">{connector.address}</p>
                      <p className="text-muted-foreground truncate">
                        {connector.city}, {connector.state} -{" "}
                        {connector.pincode}
                      </p>
                    </div>
                  </div>

                  {/* Remark */}
                  {connector.remark && (
                    <div className="flex items-start gap-3 text-sm pt-2 border-t">
                      <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <p className="text-muted-foreground">
                        {connector.remark}
                      </p>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs px-2 flex-1 h-8"
                      onClick={() => {
                        const n = connector.whatsappNumber.replace(
                          /[^\d]/g,
                          "",
                        );
                        window.open(
                          `tel:+${n.startsWith("91") && n.length > 10 ? n : "91" + n}`,
                        );
                      }}
                    >
                      <Phone className="h-3.5 w-3.5 mr-1" /> Call
                    </Button>
                    <Button
                      size="sm"
                      variant="success"
                      className="text-xs px-2 flex-1 h-8"
                      onClick={() => {
                        const n = connector.whatsappNumber.replace(
                          /[^\d]/g,
                          "",
                        );
                        window.open(
                          `https://wa.me/${n.startsWith("91") && n.length > 10 ? n : "91" + n}`,
                        );
                      }}
                    >
                      <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="text-xs px-3 shrink-0 h-8"
                      onClick={() => {
                        if (connector.location) {
                          window.open(
                            `https://www.google.com/maps?q=${connector.location.lat},${connector.location.lng}`,
                          );
                        } else {
                          window.open(
                            `https://www.google.com/maps/search/${encodeURIComponent(connector.address + ", " + connector.city)}`,
                          );
                        }
                      }}
                    >
                      <Navigation className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Shared With Section */}
              {connector.sharedWith && connector.sharedWith.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Share2 className="h-4 w-4" />
                      Shared With
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {connector.sharedWith.map((userId) => {
                        const sharedUser = availableUsers.find(
                          (u) => (u.userId || u.id) === userId,
                        );
                        return sharedUser ? (
                          <Badge
                            key={userId}
                            variant="secondary"
                            className="text-xs inline-flex items-center gap-1 pr-1"
                          >
                            {sharedUser.name}
                            <button
                              onClick={() => handleRemoveSharedUser(userId)}
                              className="ml-1 hover:text-destructive transition-colors"
                              title="Remove access"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Visit & Follow-up Stats */}
              <VisitFollowUpCard
                connector={connector}
                setConnector={setConnector}
                updatePartner={updatePartner}
              />

              {/* Associated Reminders */}
              <AssociatedTasks entityType="vendor" entityId={connector.id} />

              {/* Associated Leads */}
              <AssociatedLeads entityType="connector" entityId={connector.id} />

              {/* Documents / Visiting Cards Section */}
              {connector.documents && connector.documents.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      Documents / Visiting Cards ({connector.documents.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {connector.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50 group"
                        >
                          <div
                            className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                            onClick={() => {
                              setViewingDocument(doc);
                              setDocIframeLoading(true);
                              setDocIframeError(false);
                            }}
                          >
                            <div className="h-10 w-10 rounded bg-background flex items-center justify-center shrink-0">
                              {doc.type.startsWith("image/") ? (
                                <img
                                  src={doc.url}
                                  alt={doc.name}
                                  className="h-10 w-10 rounded object-cover"
                                />
                              ) : (
                                <FileText className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">
                                {doc.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {doc.type.startsWith("image/")
                                  ? "Image"
                                  : doc.type.includes("pdf")
                                    ? "PDF"
                                    : "Document"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                setViewingDocument(doc);
                                setDocIframeLoading(true);
                                setDocIframeError(false);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <a
                              href={doc.url}
                              download={doc.name}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent/10 transition-colors"
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Deactivation Status & Button */}
              {connector.isActive === false && (
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-red-700">Deactivated</p>
                        {connector.deactivationReason && (
                          <p className="text-sm text-red-600 mt-1">
                            Reason: {connector.deactivationReason}
                          </p>
                        )}
                        {connector.deactivatedAt && (
                          <p className="text-xs text-red-500 mt-1">
                            Since{" "}
                            {format(connector.deactivatedAt, "dd MMM yyyy")}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="pt-2 pb-4">
                {connector.isActive !== false ? (
                  <Button
                    variant="destructive"
                    className="w-full h-12"
                    onClick={() => setDeactivateDialogOpen(true)}
                    disabled={isDeactivating}
                  >
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Deactivate Partner
                  </Button>
                ) : (
                  <Button
                    variant="success"
                    className="w-full h-12"
                    onClick={handleDeactivateToggle}
                    disabled={isDeactivating}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {isDeactivating ? "Reactivating..." : "Reactivate Partner"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit Sheet */}
        <Sheet open={isEditing} onOpenChange={setIsEditing}>
          <SheetContent
            side="bottom"
            className="h-[90vh] rounded-t-3xl flex flex-col"
          >
            <SheetHeader className="pb-4 flex-shrink-0">
              <SheetTitle className="text-xl font-bold">
                Edit Partner Details
              </SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6">
              <div className="space-y-5 pb-8">
                {/* SECTION: Basic Information */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <User className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">
                      Basic Information
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Name *</Label>
                    <Input
                      value={editForm.name || ""}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, name: e.target.value }))
                      }
                      placeholder="Ex - Rajesh Kumar"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Type</Label>
                    <Select
                      value={editForm.type}
                      onValueChange={(v) =>
                        setEditForm((p) => ({ ...p, type: v as DsaPartnerType }))
                      }
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DSA_PARTNER_TYPE_LABELS).map(
                          ([key, label]) => (
                            <SelectItem key={key} value={key}>
                              {label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* SECTION: Contact Details */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <Phone className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">
                      Contact Details
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">WhatsApp *</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
                        +91
                      </span>
                      <Input
                        type="tel"
                        value={(editForm.whatsappNumber || "").replace(
                          /^\+91/,
                          "",
                        )}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            whatsappNumber: e.target.value.replace(/\D/g, ""),
                          }))
                        }
                        maxLength={10}
                        placeholder="Ex - 9876543210"
                        className="h-12 rounded-l-none"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email</Label>
                    <Input
                      type="email"
                      value={editForm.email || ""}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, email: e.target.value }))
                      }
                      placeholder="Ex - rajesh@email.com"
                      className="h-12"
                    />
                  </div>
                </div>

                {/* SECTION: Address Details */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <Building className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">
                      Address Details
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Address</Label>
                    <Input
                      value={editForm.address || ""}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, address: e.target.value }))
                      }
                      placeholder="Ex - 123, ABC Road, Near XYZ Mall"
                      className="h-12"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">City</Label>
                      <SearchableSelect
                        options={cityOptions}
                        value={editForm.city || ""}
                        onValueChange={(val) =>
                          setEditForm((p) => ({ ...p, city: val }))
                        }
                        placeholder="Ex - Mumbai"
                        searchPlaceholder="Search cities..."
                        emptyText="No city found."
                        className="h-12"
                        fullWidthPopover
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Pincode</Label>
                      <Input
                        type="tel"
                        value={editForm.pincode || ""}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            pincode: e.target.value.replace(/\D/g, ""),
                          }))
                        }
                        maxLength={6}
                        placeholder="Ex - 400001"
                        className="h-12"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">State *</Label>
                    <Select
                      value={editForm.state || ""}
                      onValueChange={(value) =>
                        setEditForm((p) => ({ ...p, state: value }))
                      }
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDIAN_STATES.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* SECTION: Notes */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <FileText className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">Notes</h3>
                  </div>
                  <div className="space-y-2">
                    <Textarea
                      value={editForm.remark || ""}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, remark: e.target.value }))
                      }
                      placeholder="Ex - Deals in residential properties, Andheri area specialist"
                      rows={3}
                      className="resize-none"
                    />
                  </div>
                </div>

                {/* SECTION: Location */}
                {canEditLocation && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 pb-1">
                      <MapPin className="h-4 w-4 text-green-600" />
                      <h3 className="font-semibold text-green-800">Location</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1 h-12"
                          onClick={() => setEditMapPickerOpen(true)}
                        >
                          <Map className="h-4 w-4 mr-2" />
                          {editForm.location
                            ? "Location Set ✓ — Change"
                            : "Update Location on Map"}
                        </Button>
                      </div>
                      {editForm.location && (
                        <p className="text-xs text-muted-foreground">
                          📍 {editForm.location.lat.toFixed(6)},{" "}
                          {editForm.location.lng.toFixed(6)}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* SECTION: Documents */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <FileText className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">Documents</h3>
                  </div>
                  <div className="space-y-2">
                    {/* Existing Documents (Already Uploaded) */}
                    {connector.documents && connector.documents.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Uploaded Documents:
                        </p>

                        {/* Existing Images Grid */}
                        {connector.documents.filter((d) =>
                          d.type.startsWith("image/"),
                        ).length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            {connector.documents
                              .filter((d) => d.type.startsWith("image/"))
                              .map((doc, index) => {
                                const isMarkedForDeletion =
                                  documentsToDelete.includes(doc.id);
                                return (
                                  <div
                                    key={`existing-img-${doc.id}`}
                                    className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
                                      isMarkedForDeletion
                                        ? "border-destructive opacity-50"
                                        : "border-primary/20"
                                    }`}
                                  >
                                    <img
                                      src={doc.url}
                                      alt={doc.name}
                                      className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        window.open(doc.url, "_blank");
                                      }}
                                    />
                                    <button
                                      onClick={() =>
                                        handleMarkDocumentForDeletion(doc.id)
                                      }
                                      className="absolute top-1 right-1 bg-destructive/80 text-destructive-foreground rounded-full p-1.5 hover:bg-destructive transition-colors shadow-lg"
                                      type="button"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                    <div
                                      className={`absolute bottom-0 left-0 right-0 text-xs p-1 text-center ${
                                        isMarkedForDeletion
                                          ? "bg-destructive text-destructive-foreground"
                                          : "bg-primary/80 text-primary-foreground"
                                      }`}
                                    >
                                      {isMarkedForDeletion
                                        ? "Will be deleted"
                                        : "Uploaded ✓"}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}

                        {/* Existing Non-Image Files */}
                        {connector.documents.filter(
                          (d) => !d.type.startsWith("image/"),
                        ).length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {connector.documents
                              .filter((d) => !d.type.startsWith("image/"))
                              .map((doc) => {
                                const isMarkedForDeletion =
                                  documentsToDelete.includes(doc.id);
                                return (
                                  <Badge
                                    key={`existing-doc-${doc.id}`}
                                    variant={
                                      isMarkedForDeletion
                                        ? "destructive"
                                        : "secondary"
                                    }
                                    className="flex items-center gap-1.5 py-1.5 px-2.5"
                                  >
                                    <FileText className="h-3.5 w-3.5" />
                                    <span
                                      className="max-w-[100px] truncate text-xs cursor-pointer"
                                      onClick={() =>
                                        window.open(doc.url, "_blank")
                                      }
                                    >
                                      {doc.name}
                                    </span>
                                    <button
                                      onClick={() =>
                                        handleMarkDocumentForDeletion(doc.id)
                                      }
                                      type="button"
                                      className="ml-0.5"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </Badge>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    )}

                    <FileUploadSection
                      files={fileManagerFiles}
                      onCameraCapture={fileUploadManager.handleCameraCapture}
                      onFileUpload={fileUploadManager.handleFileUpload}
                      onRemoveFile={fileUploadManager.handleRemovePendingFile}
                      onImageClick={fileUploadManager.openImageViewer}
                      imageViewerState={{
                        isOpen: fileUploadManager.imageViewerState.isOpen,
                        images: fileUploadManager.imageViewerState.images,
                        currentIndex:
                          fileUploadManager.imageViewerState.currentIndex,
                        setCurrentIndex:
                          fileUploadManager.imageViewerState.setCurrentIndex,
                        onClose: fileUploadManager.closeImageViewer,
                      }}
                      label="New Files to Upload"
                      helpText="Photos, documents, PDFs, etc."
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveEdit}
                  className="w-full h-12 bg-gradient-to-r from-[#2d5a6b] to-[#4ea39f] hover:from-[#234851] hover:to-[#3d8985] text-white font-semibold"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* KYC Confirmation Dialog */}
        <AlertDialog
          open={kycConfirmDialogOpen}
          onOpenChange={setKycConfirmDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark KYC as Verified?</AlertDialogTitle>
              <AlertDialogDescription>
                This will mark the KYC as verified for {connector.name}. Please
                ensure all KYC documents have been reviewed and approved.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmKYCDone}>
                Yes, Mark as Verified
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Image Viewer for Existing Documents */}
        {viewingExistingImageIndex !== null && connector.documents && (
          <ImageViewer
            images={connector.documents
              .filter((d) => d.type.startsWith("image/"))
              .map((d) => d.url)}
            currentIndex={viewingExistingImageIndex}
            isOpen={viewingExistingImageIndex !== null}
            onClose={() => setViewingExistingImageIndex(null)}
            onIndexChange={setViewingExistingImageIndex}
          />
        )}

        {/* Rich Document Viewer Dialog */}
        <Dialog
          open={viewingDocument !== null}
          onOpenChange={(open) => {
            if (!open) {
              setViewingDocument(null);
              setDocIframeError(false);
              setDocImageError(false);
              setDocIframeLoading(true);
              docResetZoom();
            }
          }}
        >
          <DialogContent className="max-w-5xl w-[calc(100vw-1rem)] max-h-[95dvh] p-0 gap-0 overflow-hidden [&>.absolute]:hidden">
            <DialogTitle className="sr-only">
              Preview {viewingDocument?.name}
            </DialogTitle>
            {viewingDocument && (
              <>
                {/* Viewer Header */}
                <div className="flex items-center gap-2 px-3 py-2.5 border-b bg-card/95 backdrop-blur-sm">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <h3 className="font-semibold text-sm flex-1 min-w-0 truncate">
                    {viewingDocument.name}
                  </h3>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Zoom Controls */}
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          setDocZoomLevel((prev) => Math.max(0.25, prev - 0.25))
                        }
                        title="Zoom Out"
                      >
                        <ZoomOut className="h-3.5 w-3.5" />
                      </Button>
                      <span className="text-xs font-medium min-w-[3rem] text-center">
                        {Math.round(docZoomLevel * 100)}%
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          setDocZoomLevel((prev) => Math.min(3, prev + 0.25))
                        }
                        title="Zoom In"
                      >
                        <ZoomIn className="h-3.5 w-3.5" />
                      </Button>
                      {docZoomLevel !== 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setDocZoomLevel(1)}
                          title="Reset Zoom"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    <a
                      href={viewingDocument.url}
                      download={viewingDocument.name}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 px-2"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setViewingDocument(null);
                        setDocIframeError(false);
                        setDocIframeLoading(true);
                        docResetZoom();
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Viewer Content */}
                <div className="p-2 overflow-auto max-h-[calc(95dvh-52px)] flex items-center justify-center">
                  {viewingDocument.type.startsWith("image/") ? (
                    docImageError ? (
                      <div className="text-center py-12">
                        <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
                        <h4 className="font-medium mb-2">
                          Unable to load image
                        </h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          The file may not be accessible or may have been
                          deleted.
                        </p>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            onClick={() =>
                              window.open(viewingDocument.url, "_blank")
                            }
                          >
                            <ExternalLink className="h-4 w-4 mr-2" /> Try
                            Opening Externally
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="w-full flex items-center justify-center bg-black/5 rounded-lg overflow-hidden"
                        {...docTouchHandlers}
                        style={{ touchAction: "none" }}
                      >
                        <img
                          src={viewingDocument.url}
                          alt={viewingDocument.name}
                          className="w-full max-h-[calc(95dvh-60px)] object-contain transition-transform duration-200"
                          style={{
                            transform: `scale(${docZoomLevel})`,
                            transformOrigin: "center center",
                          }}
                          onError={() => {
                            console.error(
                              "Image load failed:",
                              viewingDocument.name,
                            );
                            setDocImageError(true);
                          }}
                        />
                      </div>
                    )
                  ) : viewingDocument.type.includes("pdf") ? (
                    <div className="w-full h-[70vh] rounded-lg overflow-hidden relative">
                      {docIframeLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
                          <div className="flex flex-col items-center gap-2">
                            <Loader2Icon className="h-8 w-8 animate-spin text-primary" />
                            <p className="text-sm text-muted-foreground">
                              Loading PDF...
                            </p>
                          </div>
                        </div>
                      )}
                      <iframe
                        src={`${viewingDocument.url}#toolbar=1&navpanes=1&scrollbar=1`}
                        className="w-full h-full border-0"
                        title={viewingDocument.name}
                        onLoad={() => setDocIframeLoading(false)}
                        onError={() => {
                          setDocIframeError(true);
                          setDocIframeLoading(false);
                        }}
                      />
                    </div>
                  ) : docIframeError ? (
                    <div className="text-center py-12">
                      <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
                      <h4 className="font-medium mb-2">
                        Unable to preview document
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        The file may not be accessible, may have been deleted,
                        or requires direct access.
                        <br className="my-2" />
                        <span className="text-xs">
                          Check if you have permission to access all shared
                          files.
                        </span>
                      </p>
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <Button
                          onClick={() =>
                            window.open(viewingDocument.url, "_blank")
                          }
                          variant="default"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" /> Download
                        </Button>
                        <Button
                          onClick={() => setViewingDocument(null)}
                          variant="outline"
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-[70vh] rounded-lg overflow-hidden relative">
                      {docIframeLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
                          <div className="flex flex-col items-center gap-2">
                            <Loader2Icon className="h-8 w-8 animate-spin text-primary" />
                            <p className="text-sm text-muted-foreground">
                              Loading document...
                            </p>
                          </div>
                        </div>
                      )}
                      <iframe
                        src={`https://docs.google.com/viewer?url=${encodeURIComponent(viewingDocument.url)}&embedded=true`}
                        className="w-full h-full border-0"
                        title={viewingDocument.name}
                        onLoad={() => setDocIframeLoading(false)}
                        onError={() => {
                          setDocIframeError(true);
                          setDocIframeLoading(false);
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Share Partner Dialog */}
        <SharePartnerDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          partner={connector}
        />

        {/* Deactivation Reason Dialog */}
        <AlertDialog
          open={deactivateDialogOpen}
          onOpenChange={(open) => {
            setDeactivateDialogOpen(open);
            if (!open) setDeactivationReason("");
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate {connector.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This partner will be moved to the Deactive tab and excluded from
                all listings and filters. You can reactivate them later.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2">
              <Label className="text-sm font-medium">
                Reason for deactivation *
              </Label>
              <Textarea
                value={deactivationReason}
                onChange={(e) => setDeactivationReason(e.target.value)}
                placeholder="Enter reason for deactivation..."
                rows={3}
                className="mt-2 resize-none"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeactivateToggle}
                disabled={!deactivationReason.trim() || isDeactivating}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeactivating ? "Deactivating..." : "Deactivate"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Map Location Picker for Edit Form */}
        <MapLocationPicker
          open={editMapPickerOpen}
          onOpenChange={setEditMapPickerOpen}
          initialLocation={editForm.location}
          onLocationSelect={(location) => {
            setEditForm((p) => ({ ...p, location }));
            toast.success("Location selected from map");
          }}
        />
      </div>
    </AppLayout>
  );
}
